/**
 * services/streakService.ts
 * Centralized service for streak tracking logic.
 * Handles D1 interactions, LocalStorage caching, and synchronization.
 */
import { dbManager } from './db_manager';
import { getLocalISOString } from '../utils/timezoneUtils';

export interface StreakData {
    user_id: string;
    current_streak: number;
    highest_streak: number;
    streak_freeze_count: number;
    last_computed_date: string; // YYYY-MM-DD
    last_activity_type: string;
    updated_at?: string;
}

const CACHE_KEY_PREFIX = 'libritui_streak_';
const SYNC_QUEUE_KEY = 'libritui_streak_sync_queue';

export const streakService = {
    /**
     * Get the current streak data for a user.
     * Tries local cache first, then D1.
     */
    async getStreak(userId: string): Promise<StreakData | null> {
        try {
            const results = await dbManager.from('streaks', userId).select({ user_id: userId });
            if (results && results.length > 0) {
                const data = results[0];
                this._cacheStreak(userId, data);
                return data;
            }
        } catch (error) {
            console.warn('[StreakService] Failed to fetch streak from D1, using cache if available.', error);
        }

        // Try local cache fallback for offline/error response
        const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}${userId}`);
        if (cached) {
            try {
                return JSON.parse(cached);
            } catch (e) {
                console.error('[StreakService] Error parsing cached streak:', e);
            }
        }

        return null;
    },

    /**
     * Validates and increments the streak if not already done today.
     * Activity Type: 'flashcard', 'pronunciation', 'writing', 'reading'
     */
    async validateAndIncrementStreak(userId: string, activityType: string, offlineLocalDate?: string): Promise<{ newlyIncremented: boolean; streak: number }> {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
        const localDate = offlineLocalDate || new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
        
        const streakData = await this.getStreak(userId);

        // Idempotency check: Already computed for this local date?
        if (streakData && streakData.last_computed_date === localDate && !offlineLocalDate) {
            console.log(`[StreakService] Already computed for local date (${localDate}). Skipping.`);
            return { newlyIncremented: false, streak: streakData.current_streak };
        }

        try {
            // Dispara a requisição para a nova rota blindada da API (Cloudflare Worker)
            const response = await dbManager.execute({
                action: 'increment_streak',
                table: 'streaks',
                user_id: userId,
                data: {
                    timezone,
                    activity_type: activityType,
                    local_date: localDate
                }
            }) as any;

            if (response && response.streak !== undefined) {
                const updatedData: StreakData = {
                    user_id: userId,
                    current_streak: response.streak,
                    highest_streak: response.highest_streak,
                    streak_freeze_count: streakData?.streak_freeze_count || 0,
                    last_computed_date: response.last_computed_date,
                    last_activity_type: activityType,
                    updated_at: getLocalISOString()
                };

                // Update UI/Cache immediately with server absolute truth
                this._cacheStreak(userId, updatedData);
                
                // Clear calendar cache to force refetch next time calendar is opened
                try {
                    const cachePrefix = `libritui_calendar_cache_${userId}_`;
                    Object.keys(localStorage).forEach(key => {
                        if (key.startsWith(cachePrefix)) {
                            localStorage.removeItem(key);
                        }
                    });
                } catch (cacheErr) {
                    console.error("[StreakService] Failed to clear calendar cache:", cacheErr);
                }
                
                // If we were offline and had a queue, this success means we can try syncing others
                this.syncStreaks(userId);
                
                return { newlyIncremented: response.newlyIncremented, streak: response.streak };
            }
            
            throw new Error("Invalid response from increment_streak API");

        } catch (error) {
            console.warn('[StreakService] Offline or API error. Simulating locally and queuing for sync.', error);
            
            // Simulação Local Segura (Apenas para não travar a UI)
            let newStreak = 1;
            let highestStreak = 1;

            if (streakData) {
                newStreak = streakData.current_streak;
                highestStreak = streakData.highest_streak;
                const lastDate = streakData.last_computed_date;

                if (lastDate) {
                    const [y1, m1, d1] = lastDate.split('-').map(Number);
                    const [y2, m2, d2] = localDate.split('-').map(Number);
                    const last = new Date(y1, m1 - 1, d1);
                    const now = new Date(y2, m2 - 1, d2);
                    const diffDays = Math.round((now.getTime() - last.getTime()) / (1000 * 60 * 60 * 24));

                    if (diffDays >= 1) newStreak += 1;
                }
                highestStreak = Math.max(newStreak, highestStreak);
            }

            const simulatedData: StreakData = {
                user_id: userId,
                current_streak: newStreak,
                highest_streak: highestStreak,
                streak_freeze_count: streakData?.streak_freeze_count || 0,
                last_computed_date: localDate,
                last_activity_type: activityType,
                updated_at: getLocalISOString()
            };

            this._cacheStreak(userId, simulatedData);
            
            // Queues with the STRICT local timestamp of when the action happened
            this._queueForSync(userId, simulatedData);
            
            return { newlyIncremented: true, streak: newStreak };
        }
    },

    /**
     * Sync any queued streak updates to D1.
     */
    async syncStreaks(userId: string) {
        const queue = this._getQueue();
        if (queue.length === 0) return;

        console.log(`[StreakService] Syncing ${queue.length} queued updates...`);
        const remainingQueue: StreakData[] = [];

        for (const item of queue) {
            if (item.user_id !== userId) {
                remainingQueue.push(item);
                continue;
            }

            try {
                // Ao invés de fazer UPSERT manual e cego, pedimos pra API validar
                // enviando a data local exata em que a atividade ocorreu (item.last_computed_date)
                await this.validateAndIncrementStreak(userId, item.last_activity_type, item.last_computed_date);
            } catch (e) {
                console.error('[StreakService] Sync failed for item:', item, e);
                remainingQueue.push(item);
            }
        }

        localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(remainingQueue));
    },

    _cacheStreak(userId: string, data: StreakData) {
        localStorage.setItem(`${CACHE_KEY_PREFIX}${userId}`, JSON.stringify(data));
        // Flag to prevent blinking UI on load
        if (data.last_computed_date) {
            localStorage.setItem('libritui_streak_done_today', data.last_computed_date);
        }
    },

    _getQueue(): StreakData[] {
        const queueJson = localStorage.getItem(SYNC_QUEUE_KEY);
        try {
            return queueJson ? JSON.parse(queueJson) : [];
        } catch {
            return [];
        }
    },

    _queueForSync(userId: string, data: StreakData) {
        const queue = this._getQueue();
        // Replace if same date exists to avoid duplicates
        const index = queue.findIndex(q => q.user_id === userId && q.last_computed_date === data.last_computed_date);
        if (index !== -1) {
            queue[index] = data;
        } else {
            queue.push(data);
        }
        localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
    }
};
